import React, { useState } from "react";
import styles from "./styles.module.scss";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { addOrUpdateApiToken } from "../../../../../services/api/apiToken/ApiTokenService";
import { IApiTokenBody } from "../../../../../@types/api-token.type";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import FileCopy from "@mui/icons-material/FileCopy";
import closeIconModal from "./../../../../../assets/_CloseIconModal_.svg";
import { storageSelectedTenantGet } from "../../../../../storage/storageSelectedTenant";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";

const createApiTokenSchema = z.object({
  name: z
    .string()
    .min(1, "O nome é obrigatório")
    .max(60, "O nome deve ter no máximo 60 caracteres"),
});

interface CreateApiTokenModalProps {
  open: boolean;
  onClose: () => void;
}

export const CreateApiTokenModal: React.FC<CreateApiTokenModalProps> = ({
  open,
  onClose,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ name: string }>({
    resolver: zodResolver(createApiTokenSchema),
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);

  const onSubmit = async (data: { name: string }) => {
    const apiToken: IApiTokenBody = {
      name: data.name,
      tenantName: storageSelectedTenantGet(),
    };
    try {
      setLoading(true);
      const response = await addOrUpdateApiToken(apiToken);
      setToken(response.result);
      reset();
      setShowTokenModal(true);
      onClose();
    } catch (error) {
      console.log(error)
      toast.error("Erro ao criar o token. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (token) {
      navigator.clipboard.writeText(token);
      toast.success("Token copiado para a área de transferência!");
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box className={styles.modalBox}>
          <div className={styles.modalTitle}>
            <h3>Criar Token de API</h3>
            <Button onClick={handleClose} className={styles.iconButton}>
              {closeIconModal && <img src={closeIconModal} />}
            </Button>
          </div>

          <Controller
            name="name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Nome"
                InputLabelProps={{
                  shrink: true,
                }}
                placeholder="Digite o nome do token aqui..."
                error={!!errors.name}
                helperText={errors.name ? errors.name.message : ""}
              />
            )}
          />

          <Stack direction="row" spacing={2} className={styles.stackButton}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
              onClick={handleSubmit(onSubmit)}
              className={styles.btnCreate}
            >
              Criar
            </Button>
          </Stack>
        </Box>
      </Modal>

      <Modal open={showTokenModal} onClose={() => setShowTokenModal(false)}>
        <Box className={styles.modalBox}>
          <div className={styles.modalTitle}>
            <h3>Novo Token de API</h3>
            <Button
              onClick={() => setShowTokenModal(false)}
              className={styles.iconButton}
            >
              {closeIconModal && <img src={closeIconModal} />}
            </Button>
          </div>
          <div>
            <p className={styles.modalText}>
              Copie o novo token de API. Você não vai ver este token novamente.
            </p>
          </div>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            className={styles.tokenActions}
          >
            <TextField
              fullWidth
              value={token || ""}
              variant="outlined"
              label="Token de API"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton onClick={() => setShowToken(!showToken)}>
                    {showToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              type={showToken ? "text" : "password"}
            />
            <Button
              variant="contained"
              color="secondary"
              startIcon={<FileCopy />}
              type="button"
              onClick={copyToClipboard}
              className={styles.btnCopy}
            >
              Copiar
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            className={styles.stackButtonFinish}
          >
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={() => setShowTokenModal(false)}
              className={styles.btnFinish}
            >
              Concluído
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};
